import React from 'react';
import { BrainCircuit, Database, ImageIcon, Search } from 'lucide-react';

export type ProviderMeta = {
  label: string;
  color: string;
  description: string;
  icon: React.ReactNode;
};

const providerMetaMap: Record<string, ProviderMeta> = {
  openrouter: {
    label: 'OpenRouter',
    color: 'green',
    description: 'LLM orchestration for strategy and writing',
    icon: <BrainCircuit size={14} />,
  },
  exa: {
    label: 'Exa',
    color: 'blue',
    description: 'Web research and authority-source discovery',
    icon: <Search size={14} />,
  },
  'fal.ai': {
    label: 'fal.ai',
    color: 'purple',
    description: 'Image generation and media processing',
    icon: <ImageIcon size={14} />,
  },
  dataforseo: {
    label: 'DataForSEO',
    color: 'orange',
    description: 'Competitor blog extraction and SEO data collection',
    icon: <Database size={14} />,
  },
};

export function getProviderMeta(provider: string): ProviderMeta {
  return (
    providerMetaMap[provider] ?? {
      label: provider,
      color: 'default',
      description: 'Tracked API provider',
      icon: <Database size={14} />,
    }
  );
}

export function formatProviderCount(provider: string, count: number): string {
  if (provider === 'exa') {
    return `${count} Exa ${count === 1 ? 'search' : 'searches'}`;
  }
  if (provider === 'fal.ai') {
    return `${count} ${count === 1 ? 'image' : 'images'}`;
  }
  if (provider === 'dataforseo') {
    return `${count} DataForSEO ${count === 1 ? 'call' : 'calls'}`;
  }
  return `${count} ${count === 1 ? 'call' : 'calls'}`;
}

export function formatCurrency(value?: number): string {
  if (typeof value !== 'number') return '$0.00';
  return `$${value.toFixed(value > 0 && value < 1 ? 4 : 2)}`;
}

export function formatDuration(durationSeconds?: number): string {
  if (!durationSeconds || durationSeconds <= 0) return 'In progress';
  const minutes = Math.floor(durationSeconds / 60);
  const seconds = Math.floor(durationSeconds % 60);
  if (!minutes) return `${seconds}s`;
  return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
}

export function getRunStatusColor(status: string): string {
  if (status === 'running') return 'blue';
  if (status === 'completed') return 'green';
  if (status === 'failed') return 'red';
  if (status === 'aborted') return 'orange';
  return 'default';
}

export function getPhaseLabel(phase?: string): string {
  if (!phase) return 'Queued';
  const normalized = phase.replace(/_/g, ' ');
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}
